import { Newspaper } from 'lucide-react';
import { useNews } from '../../hooks/useWidgetsData';
import { useSettings } from '../../store/settings';
import { useArticle } from '../ArticleModal';

/**
 * Continuous right-to-left headline ticker. Content is duplicated for a seamless loop.
 * Headlines open the story pop-up; the ticker pauses while the pointer is over it.
 */
export function NewsTicker() {
  const speed = useSettings((s) => s.news.speed); // pixels per second
  const open = useArticle((s) => s.open);
  const { data } = useNews();
  if (!data?.length) return null;

  const items = data.slice(0, 20);
  // Rough width estimate keeps speed constant regardless of headline count.
  const chars = items.reduce((n, i) => n + i.title.length + i.source.length + 8, 0);
  const durationSec = Math.max(20, (chars * 9) / Math.max(10, speed));

  const row = (copy: boolean) =>
    items.map((i, idx) => (
      <button
        key={`${idx}-${i.link ?? i.title}`}
        onClick={() => open(i)}
        tabIndex={copy ? -1 : 0}
        className="mx-6 inline-flex items-center gap-2 whitespace-nowrap rounded hover:underline focus-visible:underline"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">{i.source}</span>
        <span>{i.title}</span>
      </button>
    ));

  return (
    <div className="glass flex items-center overflow-hidden !rounded-none !border-x-0 py-2" aria-label="News headlines">
      <div className="z-10 flex shrink-0 items-center bg-[var(--surface-strong)] px-4 py-1">
        <Newspaper className="h-5 w-5" aria-hidden />
      </div>
      <div className="relative min-w-0 flex-1 overflow-hidden">
        <div
          className="inline-flex text-lg hover:[animation-play-state:paused] focus-within:[animation-play-state:paused]"
          style={{ animation: `ticker ${durationSec}s linear infinite` }}
        >
          <div className="inline-flex">{row(false)}</div>
          <div className="inline-flex" aria-hidden>
            {row(true)}
          </div>
        </div>
      </div>
    </div>
  );
}
